import { useRef, useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type MensajeProveedor, type MensajesProveedores, type VariablePropia } from "@/lib/api";
import {
  armarMensaje,
  etiquetasSinMensaje,
  insertarVariable,
  nombresVariables,
  validarNombreVariable,
  validarTextoMensaje,
  valoresMensaje,
} from "@/lib/mensajes";

export const GENERAL = "general";

type Dialogo =
  | { tipo: "variable"; editando: VariablePropia | null }
  | { tipo: "mensaje"; editando: MensajeProveedor | null }
  | { tipo: "borrar-variable"; variable: VariablePropia }
  | { tipo: "borrar-mensaje"; mensaje: MensajeProveedor }
  | null;

/**
 * Lógica de Configuración → Mensajes a proveedores (sin JSX). Sólo admin. Variables propias (nombre para todos los
 * desarrollos, valor de éste), las del proyecto (salen de lo cargado) y un mensaje por etiqueta más el general. Cada
 * escritura devuelve el estado entero, que reemplaza al que había.
 */
export function useMensajesProveedoresTab() {
  const { fid } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/mensajes-proveedores`;
  const recurso = useApiResource<MensajesProveedores>(base);
  const [nuevo, setNuevo] = useState<MensajesProveedores | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [nombre, setNombre] = useState("");
  const [valor, setValor] = useState("");
  const [para, setPara] = useState<string | undefined>(undefined);
  const [texto, setTexto] = useState("");
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const textoRef = useRef<HTMLTextAreaElement>(null);

  const estado = nuevo ?? recurso.data ?? null;

  const accionVariable = useAsyncAction((editando: VariablePropia | null, n: string, v: string) => {
    const datos = { nombre: n.trim(), valor: v.trim() || null };
    return editando ? api.patch<MensajesProveedores>(`${base}/variables/${editando.id}`, datos) : api.post<MensajesProveedores>(`${base}/variables`, datos);
  });
  const accionMensaje = useAsyncAction((p: string, t: string) =>
    api.put<MensajesProveedores>(`${base}/mensajes`, { etiqueta_id: p === GENERAL ? null : Number(p), texto: t.trim() }),
  );
  const accionBorrar = useAsyncAction((ruta: string) => api.delete<MensajesProveedores>(ruta));

  const cerrar = () => setDialogo(null);
  const listo = (e: MensajesProveedores | null | undefined) => {
    if (!e) return;
    setNuevo(e);
    cerrar();
  };

  const errorNombre = estado && dialogo?.tipo === "variable" ? validarNombreVariable(nombre, estado, dialogo.editando?.id ?? null) : null;
  const errorTexto = validarTextoMensaje(texto);
  const errorPara = para ? null : "Elegí para qué etiqueta es";

  const abrirVariable = (editando: VariablePropia | null) => {
    setNombre(editando?.nombre ?? "");
    setValor(editando?.valor ?? "");
    setMostrarErrores(false);
    accionVariable.limpiarError();
    setDialogo({ tipo: "variable", editando });
  };

  const abrirMensaje = (editando: MensajeProveedor | null) => {
    const hayGeneral = estado?.mensajes.some((m) => m.etiqueta_id === null) ?? false;
    setPara(editando ? (editando.etiqueta_id === null ? GENERAL : String(editando.etiqueta_id)) : hayGeneral ? undefined : GENERAL);
    setTexto(editando?.texto ?? "");
    setMostrarErrores(false);
    accionMensaje.limpiarError();
    setDialogo({ tipo: "mensaje", editando });
  };

  const guardarVariable = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "variable" || errorNombre) return;
    listo(await accionVariable.run(dialogo.editando, nombre, valor));
  };

  const guardarMensaje = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "mensaje" || errorTexto || !para) return;
    listo(await accionMensaje.run(para, texto));
  };

  const insertar = (variable: string) => {
    const el = textoRef.current;
    const inicio = el?.selectionStart ?? texto.length;
    const fin = el?.selectionEnd ?? texto.length;
    const r = insertarVariable(texto, variable, inicio, fin);
    setTexto(r.texto);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(r.cursor, r.cursor);
    });
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo === "borrar-variable") listo(await accionBorrar.run(`${base}/variables/${dialogo.variable.id}`));
    if (dialogo?.tipo === "borrar-mensaje") listo(await accionBorrar.run(`${base}/mensajes/${dialogo.mensaje.id}`));
  };

  // Para elegir en el mensaje: el general (si no hay otro o es el que se edita) y las etiquetas sin mensaje propio.
  const editandoMensaje = dialogo?.tipo === "mensaje" ? dialogo.editando : null;
  const opcionesPara = estado
    ? [
        ...(estado.mensajes.some((m) => m.etiqueta_id === null && m.id !== editandoMensaje?.id) ? [] : [{ valor: GENERAL, etiqueta: "General (para las etiquetas sin mensaje propio)" }]),
        ...(editandoMensaje?.etiqueta_id ? [{ valor: String(editandoMensaje.etiqueta_id), etiqueta: editandoMensaje.etiqueta ?? "" }] : []),
        ...etiquetasSinMensaje(estado).map((e) => ({ valor: String(e.id), etiqueta: e.nombre })),
      ]
    : [];

  const previa = (t: string) => (estado ? armarMensaje(t, valoresMensaje(estado, null)) : null);

  return {
    estado,
    cargando: recurso.loading && !estado,
    errorCarga: estado ? null : (recurso.error?.message ?? null),
    reintentar: recurso.recargar,
    previa,
    dialogo,
    cerrar,
    // variable
    nombre,
    setNombre,
    valor,
    setValor,
    errorNombre: mostrarErrores ? errorNombre : null,
    abrirNuevaVariable: () => abrirVariable(null),
    abrirEditarVariable: abrirVariable,
    guardarVariable,
    guardandoVariable: accionVariable.loading,
    errorGuardarVariable: accionVariable.error,
    // mensaje
    para,
    setPara,
    opcionesPara,
    errorPara: mostrarErrores ? errorPara : null,
    texto,
    setTexto,
    textoRef,
    errorTexto: mostrarErrores ? errorTexto : null,
    variables: estado ? nombresVariables(estado) : [],
    insertar,
    previaTexto: previa(texto),
    abrirNuevoMensaje: () => abrirMensaje(null),
    abrirEditarMensaje: abrirMensaje,
    guardarMensaje,
    guardandoMensaje: accionMensaje.loading,
    errorGuardarMensaje: accionMensaje.error,
    // borrar
    abrirBorrarVariable: (variable: VariablePropia) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar-variable", variable });
    },
    abrirBorrarMensaje: (mensaje: MensajeProveedor) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar-mensaje", mensaje });
    },
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
